import {ChangeDetectorRef, Component, OnInit, ViewEncapsulation} from '@angular/core';
import 'jquery';
import {BoService} from '../services/bo.service';
import {TicketingService} from "../services/ticketing.service";
import {
  isFreeTicket,
  NfcAssociateEvent,
  ParticipantApiResponse,
  ParticipantDetail,
  ParticipantHistoryEntry,
  TplChangeEvent,
  TplOption,
} from './participant-detail-drawer/participant-detail-drawer.types';
import {formatRegistrationDate} from './participant-date.utils';

/** Réponse de PATCH /scan/lists/:idList/participants/bulk-tpl (api-control). */
interface BulkTplResult {
  updated?: number;
  skipped?: number;
  skippedDetails?: Array<{idParticipant: string; reason: string}>;
}

declare var $: any;
@Component({
  selector: 'app-liste-des-participants',
  templateUrl: './liste-des-participants.component.html',
  styleUrls: ['./liste-des-participants.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ListeDesParticipantsComponent implements OnInit {

  // ---- Drawer state (Angular detail panel) ----
  drawerOpen = false;
  drawerParticipant: ParticipantDetail | null = null;
  drawerTpls: TplOption[] = [];

  // Refs vers le state interne du closure ngOnInit (alimentées par initDrawerRefs)
  private _glist: any[] = [];
  private _idlist = '';
  private _dataKeys: any[] = [];
  private _refreshList: () => void = () => {};

  constructor(
    private boService: BoService,
    private ticketingService: TicketingService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  /** Alimentée depuis ngOnInit pour donner accès au state du closure jQuery. */
  initDrawerRefs(glist: any[], idlist: string, dataKeys: any[], refresh: () => void): void {
    this._glist = glist;
    this._idlist = idlist;
    this._dataKeys = dataKeys;
    this._refreshList = refresh;
  }

  /** Appelée depuis detpar() à la place de la manipulation jQuery du panel. */
  openDrawerForIndex(index: number): void {
    const p = this._glist[index];
    if (!p) return;
    this.drawerParticipant = this.mapToDetail(p);
    this.drawerTpls = [];
    this.drawerOpen = true;
    this.cdr.markForCheck();

    this.boService.getParticipant(this._idlist, p.id).then((data: ParticipantApiResponse) => {
      const fresh = data?.participant;
      if (!fresh || this.drawerParticipant?.id !== p.id) return;
      Object.assign(p, fresh);
      this.drawerParticipant = this.mapToDetail(p);
      this.cdr.markForCheck();

      // Tarifs gratuits dispo : uniquement si le billet est gratuit (cf. isFreeTicket).
      // On décide sur les données FRAÎCHES (status/canal/pricettc) — sinon isLoadingTpls reste bloqué
      // pour les billets gratuits autrement qu'à 0€ (ex: status 'Imported').
      if (isFreeTicket(p)) {
        this.boService.getFreeTplByEvent(p.idEvent).then((tplData: any) => {
          const all: TplOption[] = (tplData?.message || [])
            .filter((tpl: any) => tpl._id !== p.idTplTicket)
            .map((tpl: any) => ({ _id: tpl._id, name: tpl.name }));
          if (this.drawerParticipant?.id === p.id) {
            this.drawerTpls = all;
            this.cdr.markForCheck();
          }
        }).catch((err: any) => { console.error('[participant-drawer] action failed', err); });
      }
    }).catch(() => {});

    // PDF async
    this.ticketingService.recreateParticipant(p.idEvent, p.idTplTicket, p.id).then((result: any) => {
      if (this.drawerParticipant?.id === p.id) {
        this.drawerParticipant = { ...this.drawerParticipant, pdfUrl: result.response };
        this.cdr.markForCheck();
      }
    });

  }

  private mapToDetail(p: any): ParticipantDetail {
    const history: ParticipantHistoryEntry[] = [];
    const ins = (p.tabScanIn || []).map((s: any, i: number) => ({
      type: 'in' as const,
      title: 'Contrôle d\'entrée',
      who: this.staffName(s.idStaff),
      when: s.date,
      device: s.idStaff === 'fromBo' ? 'Backoffice' : 'Scanner',
      sortKey: this.sortKeyFromDate(s.date),
      origIndex: i,
    }));
    const outs = (p.tabScanOut || []).map((s: any, i: number) => ({
      type: 'out' as const,
      title: 'Contrôle de sortie',
      who: this.staffName(s.idStaff),
      when: s.date,
      device: s.idStaff === 'fromBo' ? 'Backoffice' : 'Scanner',
      sortKey: this.sortKeyFromDate(s.date),
      origIndex: i,
    }));
    [...ins, ...outs]
      .sort((a, b) => b.sortKey - a.sortKey)
      .forEach(h => history.push({ type: h.type, title: h.title, who: h.who, when: h.when, device: h.device }));

    return {
      id: p.id,
      accountNumber: p.id,
      lastName: p.lastNameUser || '',
      firstName: p.firstNameUser || '',
      email: p.email || '',
      status: p.status,
      canal: p.canal,
      barcode: p.keyRandom || '',
      shortTag: p.shortTag || '',
      associatedNfc: p.associatedNfc || '',
      tplName: p.nameTpl || '',
      tplOriginalName: p.nameTplOriginal || '',
      tplId: p.idTplTicket || '',
      idEvent: p.idEvent || '',
      pricettc: p.pricettc,
      pdfUrl: p.urlTicket || undefined,
      totScan: p.totScan ?? 0,
      maxScan: p.maxscan ?? 1,
      flag: p.flag === 'true' || p.flag === true,
      ban: p.ban === 'true' || p.ban === true,
      formAnswers: 'Aucune donnée',
      history,
    };
  }

  private staffName(idStaff: string): string {
    if (idStaff === 'fromBo') return 'Backoffice';
    const k = this._dataKeys.find(d => d._id === idStaff);
    return k?.name || 'Inconnu';
  }

  private sortKeyFromDate(date: string): number {
    if (!date) return 0;
    const parts = date.split(' ');
    const d = (parts[0] || '').split('-');
    const t = (parts[1] || '').replace(/:/g, '');
    return parseFloat((d[2] || '') + (d[1] || '') + (d[0] || '') + t) || 0;
  }

  private currentIndex(participantId: string): number {
    return this._glist.findIndex(p => p.id === participantId);
  }

  // ---- Drawer outputs handlers ----
  onDrawerClose(): void {
    this.drawerOpen = false;
    this.drawerParticipant = null;
    this.cdr.markForCheck();
  }

  onDrawerSave(ev: { id: string; lastName: string; firstName: string }): void {
    const idx = this.currentIndex(ev.id);
    if (idx < 0) return;
    const payload: any = { lastNameUser: ev.lastName, firstNameUser: ev.firstName };
    this.boService.editParticipant(ev.id, payload).then(() => {
      this._glist[idx].lastNameUser = ev.lastName;
      this._glist[idx].firstNameUser = ev.firstName;
      const ln = ev.lastName.charAt(0).toUpperCase() + ev.lastName.slice(1).toLowerCase();
      const fn = ev.firstName.charAt(0).toUpperCase() + ev.firstName.slice(1).toLowerCase();
      this._glist[idx].firstAndLastName = ln + ' ' + fn;
      if (this.drawerParticipant?.id === ev.id) {
        this.drawerParticipant = { ...this.drawerParticipant, lastName: ev.lastName, firstName: ev.firstName };
      }
      this._refreshList();
      this.cdr.markForCheck();
    }).catch((err: any) => { console.error('[participant-drawer] action failed', err); });
  }

  onDrawerFlagToggled(ev: { participantId: string; flag: boolean }): void {
    this.applyBooleanToggle(ev.participantId, 'flag', ev.flag);
  }

  onDrawerBanToggled(ev: { participantId: string; ban: boolean }): void {
    this.applyBooleanToggle(ev.participantId, 'ban', ev.ban);
  }

  /** Toggle d'un champ booléen sérialisé en 'true'/'false' (legacy storage). */
  private applyBooleanToggle(participantId: string, field: 'flag' | 'ban', value: boolean): void {
    const idx = this.currentIndex(participantId);
    if (idx < 0) return;
    const newVal = value ? 'true' : 'false';
    this.boService.editParticipant(participantId, { [field]: newVal }).then(() => {
      this._glist[idx][field] = newVal;
      if (this.drawerParticipant?.id === participantId) {
        this.drawerParticipant = { ...this.drawerParticipant, [field]: value };
      }
      this._refreshList();
      this.cdr.markForCheck();
    }).catch((err: any) => { console.error('[participant-drawer] action failed', err); });
  }

  onDrawerTplChanged(ev: TplChangeEvent): void {
    const idx = this.currentIndex(ev.participantId);
    if (idx < 0) return;
    this.boService.changeTpl(this._idlist, ev.participantId, ev.newTplId).then(() => {
      const p = this._glist[idx];
      if (!p.idTplTicketOriginal) {
        p.idTplTicketOriginal = p.idTplTicket;
        p.nameTplOriginal = p.nameTpl;
      }
      p.idTplTicket = ev.newTplId;
      p.nameTpl = ev.newTplName;
      if (this.drawerParticipant?.id === ev.participantId) {
        this.drawerParticipant = {
          ...this.drawerParticipant,
          tplId: ev.newTplId,
          tplName: ev.newTplName,
          tplOriginalName: p.nameTplOriginal,
        };
      }
      this._refreshList();
      this.showTplNotice('1 billet modifié', 'info');
      this.cdr.markForCheck();
    }).catch((err: any) => {
      const code = err?.error?.error?.code || err?.error?.code;
      const msg = (code === 'CURRENT_TPL_NOT_FREE' || code === 'NEW_TPL_NOT_FREE' || code === 'REFUNDED_PAID_TICKET')
        ? '1 billet payant impossible à modifier'
        : this.tplErrorLabel(code);
      this.showTplNotice(msg, 'error');
    });
  }

  onDrawerNfcAssociated(ev: NfcAssociateEvent): void {
    const idx = this.currentIndex(ev.participantId);
    if (idx < 0) return;
    const tag = ev.tag.toUpperCase();
    if (tag.length < 4) return;
    this.boService.associateNfc(this._idlist, ev.participantId, tag).then((data: any) => {
      const p = data.participant;
      if (!p) return;
      this._glist[idx].associatedNfc = p.associatedNfc || tag;
      this._glist[idx].shortTag = p.shortTag || '';
      if (this.drawerParticipant?.id === ev.participantId) {
        this.drawerParticipant = {
          ...this.drawerParticipant,
          associatedNfc: this._glist[idx].associatedNfc,
          shortTag: this._glist[idx].shortTag,
        };
      }
      this._refreshList();
      this.cdr.markForCheck();
    }).catch((err: any) => {
      const code = err?.error?.error?.code || err?.error?.error?.message || '';
      if (code === 'NFC_ALREADY_ASSOCIATED') alert('Ce tag NFC est déjà associé à un autre participant.');
      else if (code === 'NFC_BANNED') alert('Ce tag NFC est banni.');
      else if (code === 'NFC_NOT_FOUND') alert('Ce tag NFC n\'a pas été trouvé dans le système cashless.');
      else alert('Erreur lors de l\'association NFC. Veuillez réessayer.');
    });
  }

  onDrawerNfcDisassociated(ev: { participantId: string }): void {
    if (!confirm('Êtes-vous sûr de vouloir désassocier le tag NFC de ce participant ?')) return;
    const idx = this.currentIndex(ev.participantId);
    if (idx < 0) return;
    this.boService.disassociateNfc(this._idlist, ev.participantId).then(() => {
      this._glist[idx].associatedNfc = '';
      this._glist[idx].shortTag = '';
      if (this.drawerParticipant?.id === ev.participantId) {
        this.drawerParticipant = {
          ...this.drawerParticipant,
          associatedNfc: '',
          shortTag: '',
        };
      }
      this._refreshList();
      this.cdr.markForCheck();
    }).catch(() => alert('Erreur lors de la désassociation NFC. Veuillez réessayer.'));
  }

  onDrawerScanIncremented(ev: { participantId: string }): void {
    this.applyScanDelta(ev.participantId, 'in');
  }

  onDrawerScanDecremented(ev: { participantId: string }): void {
    this.applyScanDelta(ev.participantId, 'out');
  }

  /** Ajoute un scan d'entrée ('in') ou de sortie ('out') au participant. */
  private applyScanDelta(participantId: string, kind: 'in' | 'out'): void {
    const idx = this.currentIndex(participantId);
    if (idx < 0) return;
    const p = this._glist[idx];
    if (kind === 'in' && p.totScan >= p.maxscan) return;
    if (kind === 'out' && p.totScan <= 0) return;

    const fullDate = this.formatNow();
    const scans = Object.assign({}, p.scans);
    if (!scans[this._idlist]) scans[this._idlist] = {};

    if (kind === 'in') {
      const slot = scans[this._idlist];
      if (slot.nbrScan) {
        slot.nbrScan++;
        slot.dateScan.push({ date: fullDate, idStaff: 'fromBo' });
      } else {
        slot.nbrScan = 1;
        slot.dateScan = [{ date: fullDate, idStaff: 'fromBo' }];
      }
    } else {
      const slot = scans[this._idlist];
      if (slot.nbrDeScan) {
        slot.nbrDeScan++;
        slot.dateDescan.push({ date: fullDate, idStaff: 'fromBo' });
      } else {
        slot.nbrDeScan = 1;
        slot.dateDescan = [{ date: fullDate, idStaff: 'fromBo' }];
      }
    }

    this.boService.editParticipant(participantId, { scans }).then(() => {
      p.scans = scans;
      if (kind === 'in') {
        p.totScan++;
        p.totScanIn = (p.totScanIn || 0) + 1;
        p.tabScanIn = [...(p.tabScanIn || []), { date: fullDate, idStaff: 'fromBo' }];
      } else {
        p.totScan--;
        p.totScanOut = (p.totScanOut || 0) + 1;
        p.tabScanOut = [...(p.tabScanOut || []), { date: fullDate, idStaff: 'fromBo' }];
      }
      if (this.drawerParticipant?.id === participantId) {
        this.drawerParticipant = this.mapToDetail(p);
      }
      // Met à jour le visuel ligne courante (legacy)
      const cs = p.totScan === 0 ? 'cs0' : (p.totScan >= p.maxscan ? 'cs2' : 'cs1');
      $('#' + participantId).find('.circlescan').attr('class', 'circlescan ' + cs);
      $('#' + participantId).find('.nbscan').html(p.totScan);
      this.cdr.markForCheck();
    }).catch((err: any) => { console.error('[participant-drawer] action failed', err); });
  }

  private formatNow(): string {
    const d = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  /** Label FR pour une raison de skip (bulk tpl change). */
  reasonLabel(reason: string): string {
    switch(reason){
      case 'CURRENT_TPL_NOT_FREE': return 'tarif actuel payant';
      case 'SAME_TPL': return 'deja au meme tarif';
      case 'REFUNDED_PAID_TICKET': return 'billet payant rembourse';
      default: return reason;
    }
  }

  /**
   * Lignes du toast après un changement de tarif en masse. #9398 : les billets d'accréditation
   * écartés ne sont pas des billets payants, ils ont leur propre ligne.
   */
  bulkTplSummary(data: BulkTplResult | null): string[] {
    const plural = (n: number) => (n > 1 ? 's' : '');
    const updated = data?.updated ?? 0;
    const accreditation = (data?.skippedDetails ?? []).filter(d => d.reason === 'ACCREDITATION_TPL_LOCKED').length;
    const paid = (data?.skipped ?? 0) - accreditation;
    const lines = [`${updated} billet${plural(updated)} modifié${plural(updated)}`];
    if (paid > 0) lines.push(`${paid} billet${plural(paid)} payant${plural(paid)} impossible${plural(paid)} à modifier`);
    if (accreditation > 0) {
      lines.push(`${accreditation} billet${plural(accreditation)} d'accréditation : tarif modifiable uniquement dans Backstage`);
    }
    return lines;
  }

  /** Label FR pour un code d'erreur API (unit tpl change). */
  tplErrorLabel(code: string): string {
    switch(code){
      case 'CURRENT_TPL_NOT_FREE': return "Le tarif actuel n'est pas gratuit";
      case 'NEW_TPL_NOT_FREE': return "Le nouveau tarif n'est pas gratuit";
      case 'SAME_TPL': return 'Meme tarif selectionne';
      case 'TPL_EVENT_MISMATCH': return "Le tarif est d'un autre evenement";
      case 'REFUNDED_PAID_TICKET': return 'Billet payant rembourse, modification interdite';
      case 'ACCREDITATION_TPL_LOCKED': return "Billet d'accréditation : son tarif se modifie uniquement dans Backstage";
      default: return 'Erreur lors du changement de tarif';
    }
  }

  /** Toast ephemere (style savestick) — affiche un message pendant `ms` ms. */
  showTplNotice(message: string, kind: 'info'|'error' = 'info', ms: number = 4000){
    $('.tpl-notice').remove();
    var bg = kind === 'error' ? '#d9534f' : '#0d6efd';
    var $el = $('<div class="tpl-notice"></div>')
      .css({
        position: 'fixed', right: '-360px', top: '80px', zIndex: 99999,
        background: bg, color: '#fff', padding: '12px 18px', borderRadius: '6px',
        boxShadow: '0 6px 14px rgba(0,0,0,0.2)', maxWidth: '340px', fontSize: '13px',
        lineHeight: '1.4', transition: 'right 0.3s ease'
      })
      .html(message);
    $('body').append($el);
    setTimeout(function(){ $el.css({ right: '20px' }); }, 20);
    setTimeout(function(){
      $el.css({ right: '-360px' });
      setTimeout(function(){ $el.remove(); }, 400);
    }, ms);
  }
  ngOnInit() {
    let t = this;
    $(document).ready(function(){
      // --- Initialisation globale : --------------------------------------------
      $('.menubo,.headerbo').css({'display':'flex'});
      var wiw=$(window).width();
      $('#htitsec').html('Participants');
      $('#htitsec2').html('Liste des particpants');
      $('.menubs').attr('id','');$('.menubs-2').attr('id','actif1');
      $('.secmenu2bs').attr('id','');$('.2s1').attr('id','actif2');
      if(wiw>610){
        $('#htitsec,#htitsec2,#fles').css({'display':'inline'});
      }else{
        $('#htitsec2').css({'display':'inline'});
        $('#fles').css({'display':'none'});
      };
      $('.headerbo').css({'border-color':'var(--ui-color-border)','box-shadow':'0 6px 10px -1px rgba(0,0,0,0.00)'});
      $(window).scroll(function(){
        var sw= $(this).scrollTop();
        if(sw>110){
          $('.headerbo').css({'border-color':'transparent','box-shadow':'0 6px 10px -1px rgba(0,0,0,0.05)'});
        }else{
          $('.headerbo').css({'border-color':'var(--ui-color-border)','box-shadow':'0 6px 10px -1px rgba(0,0,0,0.00)'});
        };
      });
      if(wiw>1320 && wiw<=1600){
        $('.searchPar').attr('placeholder','Rechercher');
      }else if(wiw<=265){
        $('.searchPar').attr('placeholder','Rechercher');
      };
      $('body').css({'overflow':'hidden'});
      $(window).scrollTop(0);
      //--------------------------------------------------------------------------
      // --- Initialisation contenu : --------------------------------------------
      var dataList=t.boService.getCurrentList();
      var idlist=dataList._id;
      var dataListTpl=dataList.tpl;
      var dataKeys;
      var getcust=t.boService.getcust;
      var g0;
      var glist=[];
      var csvOptions = {
        fieldSeparator: ';',
        quoteStrings: '"',
        decimalseparator: '.',
        doc:'liste des participants',
        showLabels: true,
        showTitle: false,
        title: '',
        useBom: true,
        noDownload: false,
        headers: ["ID Compte", "Date de création", "Nom du particpant", "Adresse e-mail", "Nom du tarif", "N° code-barre", "N° tag cashless", "Identifiant tag cashless", "Contrôles", "Date du dernier contrôle" ]
      };
      var csvOptions2 = {
        fieldSeparator: ';',
        quoteStrings: '"',
        decimalseparator: '.',
        doc:'liste des participants 2',
        showLabels: true,
        showTitle: false,
        title: '',
        useBom: true,
        noDownload: false,
        headers: ["Nom", "Prénom", "Email commande", "Code barre", "Nom du tarif", "Prix ticket", "Informations supplémentaires-1"]
      };
      var getindex=';'
      var initgl=0;
      var lidm=[];
      var lids1=[];
      var dataEditAll;
      var epp=20; // items par page
      var p=1; // page séléctionnée
      var gl=0 // longeur de la data
      var maxP=0; // nombre de page
      var tabselp=[];
      var cus=['<span style="color:var(--ui-color-primary);background-color:var(--ui-color-alpha-teal-300-12)">','</span>'];
      t.boService.getAllKeys().then(data => {
        dataKeys=data
        if(dataKeys.length>0){
          for(var i=0;i<dataKeys.length;i++){
            if(dataKeys[i].name==undefined){
              dataKeys[i].name='Accès sans nom';
            };
          };
        };
        t.initDrawerRefs(glist, idlist, dataKeys, () => {
          fetchParticipants();
        });
      }).catch(error => {
        t.boService.errorLoad(error);
      });
      // Server-side pagination state
      var sortField='date';
      var sortOrder='desc';
      var searchTimer=null;

      function enrichParticipants(participants){
        for(var i=0;i<participants.length;i++){
          participants[i].id = participants[i]._id;
          participants[i].index = i;
          // #8257 : dateRegistered = la date sur laquelle le serveur trie (Date unique pour tous
          // les billets, guichet compris), dateRequest en repli d'affichage — cf. participant-date.utils.
          participants[i]['fullDate'] = formatRegistrationDate(participants[i]);
          if(!participants[i].email){participants[i].email=""};
          if(!participants[i].firstNameUser){participants[i].firstNameUser=""};
          if(!participants[i].lastNameUser){participants[i].lastNameUser=""};
          var firstName=participants[i].firstNameUser;
          var lastName=participants[i].lastNameUser;
          participants[i].firstAndLastName=lastName.substr(0,1).toUpperCase()+lastName.substr(1).toLowerCase()+' '+firstName.substr(0,1).toUpperCase()+firstName.substr(1).toLowerCase();
          if(!participants[i].keyRandom){participants[i].keyRandom=""};
          if(!participants[i].associatedNfc){participants[i].associatedNfc=""};
          if(!participants[i].shortTag){participants[i].shortTag=""};
          if(!participants[i].urlTicket){participants[i].urlTicket=""};
          if(!participants[i].idTplTicket){participants[i].idTplTicket=""};
          // nameTpl, maxscan, totScan, totScanIn, totScanOut are enriched by the API
          if(!participants[i].nameTpl){participants[i].nameTpl=''};
          if(!participants[i].maxscan){participants[i].maxscan=1};
          if(participants[i].totScan===undefined){participants[i].totScan=0};
          if(participants[i].totScanIn===undefined){participants[i].totScanIn=0};
          if(participants[i].totScanOut===undefined){participants[i].totScanOut=0};
          // Build tabScanIn/tabScanOut from scans data
          var tabScanIn=[];
          var tabScanOut=[];
          if(participants[i].scans && participants[i].scans[idlist]){
            if(participants[i].scans[idlist].dateScan){
              tabScanIn = tabScanIn.concat(participants[i].scans[idlist].dateScan);
            };
            if(participants[i].scans[idlist].dateDescan){
              tabScanOut = tabScanOut.concat(participants[i].scans[idlist].dateDescan);
            };
          };
          if(!participants[i].scans){participants[i].scans={}};
          participants[i].tabScanIn=tabScanIn;
          participants[i].tabScanOut=tabScanOut;
          if(!participants[i].flag){participants[i].flag='false'};
          if(!participants[i].ban){participants[i].ban='false'};
        };
        return participants;
      };

      function buildFilterParams(){
        var params: any = {};
        // Scan status filters: bul1 = not scanned, bul2 = scanned
        // Original logic applies filters sequentially (AND):
        // b1 only -> totScan==0 (notScanned)
        // b2 only -> totScan!=0 (scanned)
        // both b1+b2 -> totScan==0 AND totScan!=0 -> empty result
        if(b1on && b2on){
          params.scanStatus = 'both';
        }else if(b1on){
          params.scanStatus = 'notScanned';
        }else if(b2on){
          params.scanStatus = 'scanned';
        };
        // Ban filter: bul3
        if(b3on){
          params.ban = 'true';
        };
        // Flag filter: bul4
        if(b4on){
          params.flag = 'true';
        };
        // NFC filter: bul5
        if(b5on==1){
          params.nfcStatus = 'associated';
        }else if(b5on==2){
          params.nfcStatus = 'notAssociated';
        };
        return params;
      };

      function fetchParticipants(){
        var filterParams = buildFilterParams();
        t.boService.getParticipantsPaginated(idlist, {
          page: p,
          limit: epp,
          search: term || undefined,
          sortField: sortField,
          sortOrder: sortOrder,
          scanStatus: filterParams.scanStatus,
          flag: filterParams.flag,
          ban: filterParams.ban,
          nfcStatus: filterParams.nfcStatus
        }).then(function(result: any){
          gl = result.total || 0;
          maxP = result.totalPages || 1;
          glist = enrichParticipants(result.message || []);
          initgl = gl;
          if(gl<=0){
            var hasFilters = b1on || b2on || b3on || b4on || b5on;
            if(hasFilters || term){
              $('.contPar').html('<div class="txtnoX"><i class="far fa-info-circle"></i> &nbsp;Aucun r\u00e9sultat trouv\u00e9 pour les filtres s\u00e9lectionn\u00e9s.</div>');
            }else{
              $('.contPar').html('<div class="txtnoX"><i class="far fa-info-circle"></i> &nbsp;Vous n\'avez pas encore de participant.</div>');
            };
          };
          renderPage();
          updatePaginationUI();
          $('.blocLoader').fadeOut(300);
        }).catch(function(error){
          t.boService.errorLoad(error);
        });
      };

      function updatePaginationUI(){
        $('#mtotpar').html(gl.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " "));
        // Page buttons visibility
        if(maxP<=1){
          $('.cplp:eq(1), .cplp:eq(2)').css({'display':'none'});
        }else if(maxP==2){
          $('.cplp:eq(1)').css({'display':'none'});
          $('.cplp:eq(2)').css({'display':'inline-block'});
        }else{
          $('.cplp:eq(1)').css({'display':'inline-block'});
          $('.cplp:eq(2)').css({'display':'inline-block'});
        };
        $('.cplp:eq(2)').html(maxP);
        // Prev/Next arrows
        if(p<=1){
          $('.btnPrevLP').addClass('cplparroff');
        }else{
          $('.btnPrevLP').removeClass('cplparroff');
        };
        if(p>=maxP){
          $('.btnNextLP').addClass('cplparroff');
        }else{
          $('.btnNextLP').removeClass('cplparroff');
        };
        // Current page highlight
        if(p<=1){
          $('.cplp:eq(0)').addClass('cplpon');
          $('.cplp:eq(2)').removeClass('cplpon');
          $('.cplp:eq(1)').removeClass('cplpon').addClass('cplpoff').html('...');
        }else if(p>=maxP){
          $('.cplp:eq(0)').removeClass('cplpon');
          $('.cplp:eq(2)').addClass('cplpon');
          $('.cplp:eq(1)').removeClass('cplpon').addClass('cplpoff').html('...');
        }else{
          $('.cplp:eq(0)').removeClass('cplpon');
          $('.cplp:eq(2)').removeClass('cplpon');
          $('.cplp:eq(1)').removeClass('cplpoff').addClass('cplpon').html(p);
        };
        // Results label
        if(gl==0||gl==1){
          $('.rpp').html(gl);
        }else{
          var from = ((p-1)*epp)+1;
          var to = Math.min(p*epp, gl);
          $('.rpp').html(from+' à '+to);
        };
      };

      function renderPage(){
        var nbrlp=glist.length;
        $('.contPar').html('');
        if(nbrlp>0){
          for(var i=0;i<nbrlp;i++){
            var listyle='';
            var licheckprop='';
            if(tabselp.some(function(check){return check.id===glist[i].id})){
              listyle='color:var(--ui-color-primary);box-shadow:inset 0px 0px 50px 5px var(--ui-color-alpha-teal-300-12);';
              licheckprop='checked';
            };
            var liclass='lipar';if(i%2==0){liclass+=' lpg'};
            var likeyrandom=glist[i].keyRandom||'';
            var lishorttag=glist[i].shortTag||'';
            var lilongtag=glist[i].associatedNfc||'';
            var lidate=glist[i].fullDate||'';
            var liname=glist[i].firstAndLastName||'';
            var limail=glist[i].email||'';
            var linametpl=glist[i].nameTpl||'';
            var linbscan=glist[i].totScan||0;
            var limaxscan=glist[i].maxscan||0;
            var cs='cs0';
            if(linbscan>0){
              cs='cs1';
              if(linbscan>=limaxscan){
                cs='cs2';
              };
            };
            if(term){
              likeyrandom = highlight(likeyrandom,term);
              lishorttag = highlight(lishorttag,term);
              lilongtag = highlight(lilongtag,term);
              lidate = highlight(lidate,term);
              liname = highlight(liname,term);
              limail = highlight(limail,term);
              linametpl = highlight(linametpl,term);
            };
            if(liname.length==0){liname='...'};
            if(limail.length==0){limail='...'};
            if(likeyrandom.length==0){likeyrandom='...'};
            var listatusicons='';
            if(glist[i].ban=='true'){listatusicons+='<i class="fa-solid fa-hand iconban" title="Bloqué"></i> '};
            if(glist[i].flag=='true'){listatusicons+='<i class="fa-solid fa-flag iconflag" title="Marqué"></i> '};
            $('.contPar').append('<div class="'+liclass+'" id="'+glist[i].id+'" index="'+i+'" style="'+listyle+'"><div class="ltc ltcf"><input type="checkbox" class="insel" id="insel'+glist[i].id+'" '+licheckprop+'><label class="labsel" for="insel'+glist[i].id+'"><i class="fas fa-check icsel"></i></label></div><div class="ltc ltcdate">'+lidate+'</div><div class="ltc ltcname">'+listatusicons+liname+'</div><div class="ltc ltcmail">'+limail+'</div><div class="ltc ltctpl">'+linametpl+'</div><div class="ltc ltckey">'+likeyrandom+'</div><div class="ltc ltcnfc">'+lishorttag+'</div><div class="ltc ltcnfclong">'+lilongtag+'</div><div class="ltc ltccontrol"><div class="contcirclescan"><div class="lscan"><i class="fa-solid fa-minus"></i></div><div class="circlescan '+cs+'"><span class="nbscan">'+linbscan+'</span><span class="maxscan"> / '+limaxscan+'</span></div><div class="mscan"><i class="fa-solid fa-plus"></i></div></div></div></div>');
          };
          checkline();
          detpar();
          if(getcust.length>3){
            $('.searchPar').val(getcust);
            term=getcust;
            getcust='00';
            t.boService.getcust='0';
            fetchParticipants();
          }else if(getcust=='00'){
            getcust='0';
          };
        }else{
          if(term){
            $('.contPar').html('<div class="txtnoX"><i class="far fa-info-circle"></i> &nbsp;Aucun résultat trouvé.<br><br><span style="font-weight:800">Veuillez réessayer avec un autre mot clé.</span></div>');
          }else{
            $('.contPar').html('<div class="txtnoX"><i class="far fa-info-circle"></i> &nbsp;Vous n\'avez pas encore de participant.</div>');
          };
        };
      };

      // Initial load
      fetchParticipants();
      // Legacy wrapper: searchLP resets the page then delegates to server-side fetchParticipants
      function searchLP(){p=1;fetchParticipants()};
      function highlight(a,b){
        var splitMod=0;
        var termtxt='';
        var lis=a.split(b);
        if(lis.length==1){lis=a.split(b.toUpperCase());splitMod=1;};
        if(lis.length==1){lis=a.split(b.substr(0,1).toUpperCase()+b.substr(1));splitMod=2;};
        if(lis.length==1){lis=a.split(b.substr(0,1)+b.substr(1).toLowerCase());splitMod=3;};
        if(lis.length==1){lis=a.split(b.toLowerCase());splitMod=4;};
        if(lis.length>1){
          a='';
          for(var d=0;d<lis.length;d++){
            if(d!=(lis.length)-1){
              if(splitMod==1){termtxt=b.toUpperCase();
              }else if(splitMod==2){termtxt=b.substr(0,1).toUpperCase()+b.substr(1);
              }else if(splitMod==3){termtxt=b.substr(0,1)+b.substr(1).toLowerCase();
              }else if(splitMod==4){termtxt=b.toLowerCase();
              }else{termtxt=b;};
              a+=lis[d]+cus[0]+termtxt+cus[1];
            }else{
              a+=lis[d];
            };
          };
        };
        return a;
      };
      //--------------------------------------------------------------------------
      //--- Fonctions / Actions  : -----------------------------------------------
      $('.filter-btn').mouseover(function(){
        var tcf=$(this).attr('tag');
        if(tcf=='filter-notscanned'){
          $('.filter-tooltip').html('Non-composté');
        }else if(tcf=='filter-scanned'){
          $('.filter-tooltip').html('Composté');
        }else if(tcf=='filter-banned'){
          $('.filter-tooltip').html('Bloqué');
        }else if(tcf=='filter-flagged'){
          $('.filter-tooltip').html('Marqué');
        }else if(tcf=='filter-nfc'){
          if(b5on==1) $('.filter-tooltip').html('NFC associé');
          else if(b5on==2) $('.filter-tooltip').html('NFC non-associé');
          else $('.filter-tooltip').html('NFC');
        }else{
          $('.filter-tooltip').html('');
        };
      });
      $('.filter-btn').mouseout(function(){
        $('.filter-tooltip').html('');
      });
      var b1on=0;
      var b2on=0;
      var b3on=0;
      var b4on=0;
      var b5on=0;
      $('.filter-toggle').click(function(){
        if($(this).attr('class')=='filter-btn filter-toggle'){
          $(this).addClass('filter-toggle--open');
          $('.filter-notscanned,.filter-scanned,.filter-banned,.filter-flagged,.filter-nfc').addClass('filter-btn--visible');
        }else{
          $(this).removeClass('filter-toggle--open');
          $('.filter-notscanned,.filter-scanned,.filter-banned,.filter-flagged,.filter-nfc').removeClass('filter-btn--active').removeClass('filter-btn--visible').removeClass('filter-btn--alt');
          $('.filter-tooltip').html('');
          b1on=0;
          b2on=0;
          b3on=0;
          b4on=0;
          b5on=0;
          searchLP();
        };
      });
      $('.filter-notscanned').click(function(){
        if($(this).attr('class')=='filter-btn filter-notscanned filter-btn--visible'){
          $(this).addClass('filter-btn--active');
          b1on=1;
        }else{
          $(this).removeClass('filter-btn--active');
          b1on=0;
        };
        searchLP();
      });
      $('.filter-scanned').click(function(){
        if($(this).attr('class')=='filter-btn filter-scanned filter-btn--visible'){
          $(this).addClass('filter-btn--active');
          b2on=1;
        }else{
          $(this).removeClass('filter-btn--active');
          b2on=0;
        };
        searchLP();
      });
      $('.filter-banned').click(function(){
        if($(this).attr('class')=='filter-btn filter-banned filter-btn--visible'){
          $(this).addClass('filter-btn--active');
          b3on=1;
        }else{
          $(this).removeClass('filter-btn--active');
          b3on=0;
        };
        searchLP();
      });
      $('.filter-flagged').click(function(){
        if($(this).attr('class')=='filter-btn filter-flagged filter-btn--visible'){
          $(this).addClass('filter-btn--active');
          b4on=1;
        }else{
          $(this).removeClass('filter-btn--active');
          b4on=0;
        };
        searchLP();
      });
      $('.filter-nfc').click(function(){
        if(b5on==0 && $(this).hasClass('filter-btn--visible')){
          $(this).addClass('filter-btn--active');
          b5on=1;
          $('.filter-tooltip').html('NFC associé');
        }else if(b5on==1){
          $(this).removeClass('filter-btn--active').addClass('filter-btn--alt');
          b5on=2;
          $('.filter-tooltip').html('NFC non-associé');
        }else{
          $(this).removeClass('filter-btn--active').removeClass('filter-btn--alt');
          b5on=0;
          $('.filter-tooltip').html('');
        };
        searchLP();
      });
      function checkline(){
        $('#inallsel').change(function(){
          if($(this).is(':checked')){
            tabselp=[...glist];
            var tsl=tabselp.length;
            $('.insel').prop('checked',true);
            $('.lipar').css({'color':'var(--ui-color-primary)','box-shadow': 'inset 0px 0px 50px 5px var(--ui-color-alpha-teal-300-12)'});
            $('.contotpar').addClass('contotparextand');
            $('.cmtp').fadeOut(0);
            $('.countselmt').html(tsl).css({'opacity':'1.0'});
            $('.countseltxt').css({'width':'90px','opacity':'1.0'});
            if(tsl>1){
              $('.countseltxt').html('&nbsp; sélectionnés');
            }else{
              $('.countseltxt').html('&nbsp; sélectionné');
            };
            $('.btnaddPar').addClass('validact').html('<span class="cutresp7">Valider &nbsp;</span><i class="fas fa-check"></i>');
            $('.actlp').addClass('actlpon');
            $('.searchPar').addClass('searchParExtand');
            $('.btnExportAll').css({'display':'none'});
          }else{
            tabselp=[];
            $('.insel').prop('checked',false);
            $('.lipar').css({'color':'','box-shadow': 'inset 0px 0px 50px 5px transparent'});
            $('.contotpar').removeClass('contotparextand');
            $('.countselmt').css({'opacity':'0.0'});
            $('.countseltxt').css({'width':'1px','opacity':'0.0'});
            $('.btnaddPar').removeClass('validact');
            $('.actlp').removeClass('actlpon');
            $('.searchPar').removeClass('searchParExtand');
            $('.btnExportAll').css({'display':'block'});
            setTimeout(function(){
              $('.cmtp').fadeIn(400);
              $('.countselmt').html('');
              $('.countseltxt').html('');
              $('.btnaddPar').html('<span class="cutresp7"><i class="far fa-check-square"></i> &nbsp; </span>Aucune sélection');
            },500);
          };
        });
        $('.insel').change(function(){
          var getid=$(this).parent().parent().attr('id');
          var getindex2=$(this).parent().parent().attr('index');
          if($(this).is(':checked')){
            tabselp.push(glist[getindex2]);
            $('#'+getid+'').css({'color':'var(--ui-color-primary)','box-shadow': 'inset 0px 0px 50px 5px var(--ui-color-alpha-teal-300-12)'});
          }else{
            tabselp = $.grep(tabselp, function(e){
              return e.index != getindex2;
            });
            $('#'+getid+'').css({'color':'','box-shadow': 'inset 0px 0px 50px 5px transparent'});
          };
          var tsl=tabselp.length;
          if(tsl>=glist.length && glist.length>0){
            $('#inallsel').prop('checked',true);
          }else{
            $('#inallsel').prop('checked',false);
          };
          if(tsl>0){
            $('.contotpar').addClass('contotparextand');
            $('.cmtp').fadeOut(0);
            $('.countselmt').html(tsl).css({'opacity':'1.0'});
            $('.countseltxt').css({'width':'90px','opacity':'1.0'});
            if(tsl>1){
              $('.countseltxt').html('&nbsp; sélectionnés');
            }else{
              $('.countseltxt').html('&nbsp; sélectionné');
            };
            $('.btnaddPar').addClass('validact').html('<span class="cutresp7">Valider &nbsp;</span><i class="fas fa-check"></i>');
            $('.actlp').addClass('actlpon');
            $('.searchPar').addClass('searchParExtand');
            $('.btnExportAll').css({'display':'none'});
          }else{
            $('.contotpar').removeClass('contotparextand');
            $('.countselmt').css({'opacity':'0.0'});
            $('.countseltxt').css({'width':'1px','opacity':'0.0'});
            $('.btnaddPar').removeClass('validact');
            $('.actlp').removeClass('actlpon');
            $('.searchPar').removeClass('searchParExtand');
            $('.btnExportAll').css({'display':'block'});
            setTimeout(function(){
              $('.cmtp').fadeIn(400);
              $('.countselmt').html('');
              $('.countseltxt').html('');
              $('.btnaddPar').html('<span class="cutresp7"><i class="far fa-check-square"></i> &nbsp; </span>Aucune sélection');
            },500);
          };
        });
      };
      $('.selepp').change(function(){
        epp=parseInt($(this).val());
        p=1;
        fetchParticipants();
      });
      $('.btnPrevLP').click(function(){
        if(p>1){p--;fetchParticipants();};
      });
      $('.btnNextLP').click(function(){
        if(p<maxP){p++;fetchParticipants();};
      });
      $('.cplp:eq(0)').click(function(){
        p=1;fetchParticipants();
      });
      $('.cplp:eq(2)').click(function(){
        p=maxP;fetchParticipants();
      });
      // --- Export helpers ---
      function buildCsvRow(participant){
        let lastScanIn = '';
        let tabScanIn = [];
        let lastScanInSort = 0;
        let lastScanOut = '';
        let tabScanOut = [];
        let lastScanOutSort = 0;
        if(participant.tabScanIn && participant.tabScanIn.length>0){
          lastScanIn = participant.tabScanIn[(participant.tabScanIn.length)-1].date;
          tabScanIn = lastScanIn.split(/[- :]+/);
          lastScanInSort = parseFloat(tabScanIn[2]+tabScanIn[1]+tabScanIn[0]+tabScanIn[3]+tabScanIn[4]+tabScanIn[5]);
        };
        if(participant.tabScanOut && participant.tabScanOut.length>0){
          lastScanOut = participant.tabScanOut[(participant.tabScanOut.length)-1].date;
          tabScanOut = lastScanOut.split(/[- :]+/);
          lastScanOutSort = parseFloat(tabScanOut[2]+tabScanOut[1]+tabScanOut[0]+tabScanOut[3]+tabScanOut[4]+tabScanOut[5]);
        };
        let lastDateScan = '';
        if(lastScanInSort == 0 && lastScanOutSort == 0){
          lastDateScan = 'Aucun contrôle';
        }else if(lastScanInSort > lastScanOutSort){
          lastDateScan = 'Entré le '+(tabScanIn[0]+'/'+tabScanIn[1]+'/'+tabScanIn[2]+' à '+tabScanIn[3]+'h'+tabScanIn[4]);
        }else{
          lastDateScan = 'Sorti le '+(tabScanOut[0]+'/'+tabScanOut[1]+'/'+tabScanOut[2]+' à '+tabScanOut[3]+'h'+tabScanOut[4]);
        };
        return {
          'id':participant.id,
          'fullDate':participant.fullDate,
          'firstAndLastName':participant.firstAndLastName,
          'email':participant.email,
          'nameTpl':participant.nameTpl,
          'keyRandom':participant.keyRandom,
          'shortTag':participant.shortTag||'',
          'associatedNfc':participant.associatedNfc||'',
          'totScan': (participant.totScan||0)+'/'+(participant.maxscan||0),
          'dateScan': lastDateScan
        };
      };

      // --- Export ALL — #5368 : un seul fetch serveur (pas de pagination → ni doublon
      //     ni manquant), respecte les filtres actifs. L'ancienne version bouclait
      //     l'endpoint paginé et concaténait les pages sur un tri instable. ---
      $('.btnExportAll').click(function(){
        if($(this).hasClass('exporting')) return;
        $('.blocExportAllConfirm, .fdExportAllConfirm').fadeIn(500);
      });
      $('.fdExportAllConfirm, .closeExportAllConfirm').click(function(){
        $('.blocExportAllConfirm, .fdExportAllConfirm').fadeOut(500);
      });
      $('.btnConfirmExportAll').click(function(){
        $('.blocExportAllConfirm, .fdExportAllConfirm').fadeOut(500);
        var btn=$('.btnExportAll');
        if(btn.hasClass('exporting')) return;
        btn.addClass('exporting').html('<i class="fas fa-spinner fa-spin"></i>');

        var filterParams=buildFilterParams();
        t.boService.getAllParticipantsForExport(idlist, {
          search: term || undefined,
          scanStatus: filterParams.scanStatus,
          flag: filterParams.flag,
          ban: filterParams.ban,
          nfcStatus: filterParams.nfcStatus
        }).then(function(result: any){ // any-justifié: payload participants legacy non typé (idem reste du closure jQuery)
          var items = enrichParticipants(result.message || []);
          // Ceinture : dédup défensif par id (l'exhaustivité est garantie côté serveur).
          var seen={};
          var newtab=[];
          for(var i=0; i<items.length; i++){
            var id=items[i].id;
            if(seen[id]) continue;
            seen[id]=1;
            newtab.push(buildCsvRow(items[i]));
          };
          t.boService.createCsv(newtab,"Export - Liste participants",csvOptions);
          btn.removeClass('exporting').html('<i class="fa-duotone fa-regular fa-file-arrow-down"></i>');
        }).catch(function(error){
          console.error('[ExportAll] Error', error);
          // Remonte le message serveur sur 413 (export trop volumineux), sinon message générique.
          var serverMsg = error && error.error && error.error.message;
          var msg = (error && error.status===413 && serverMsg)
            ? serverMsg
            : 'Erreur lors de l\'export. Veuillez réessayer.';
          alert(msg);
          btn.removeClass('exporting').html('<i class="fa-duotone fa-regular fa-file-arrow-down"></i>');
        });
      });

      // --- Selection actions ---
      var checlic=0;
      $('.btnaddPar').click(function(){
        if(checlic==0){
          checlic=1;
          if(tabselp.length>0){
            lidm=[];
            $('.savestick').css({'right':'0px'});
            var vsa= $('.selactlp').val();
            if(vsa=='flag'){
              for(var i=0;i<tabselp.length;i++){
                if(glist[tabselp[i].index].flag=='true'){
                  glist[tabselp[i].index].flag='false';
                  lidm.push({'idParticipant':tabselp[i].id, 'data':[{'flag':'false'}]});
                }else{
                  glist[tabselp[i].index].flag='true';
                  lidm.push({'idParticipant':tabselp[i].id, 'data':[{'flag':'true'}]});
                };
              };
              if(lidm.length>0){
                fetchParticipants();
                dataEditAll=lidm;
                t.boService.editAllParticipants(dataEditAll).then(data => {
                  $('.savestick').css({'right':'-260px'});
                  checlic=0;
                })
                .catch(error => {
                  // faire cas erreur
                });
              }else{
                $('.savestick').css({'right':'-260px'});
                checlic=0;
              };
            }else if(vsa=='export'){
              var newtab=[];
              for(var i=0; i<tabselp.length; i++){
                newtab.push(buildCsvRow(tabselp[i]));
              };
              t.boService.createCsv(newtab,"Export - Liste participants",csvOptions);
              $('.savestick').css({'right':'-260px'});
              checlic=0;
            }else if(vsa=='export2'){
              var newtab=[];
              for(var i=0; i<tabselp.length;i++){
                newtab.push({
                  'lastNameUser':tabselp[i].lastNameUser,
                  'firstNameUser':tabselp[i].firstNameUser,
                  'email':tabselp[i].email,
                  'keyRandom':tabselp[i].keyRandom,
                  'nameTpl':tabselp[i].nameTpl,
                  'prix':'',
                  'infos':''
                });
              };
              t.boService.createCsv(newtab,"Export - Liste participants",csvOptions2);
              $('.savestick').css({'right':'-260px'});
              checlic=0;
            }else if(vsa=='compost'){
              var newDate=new Date();
              var year=newDate.getFullYear();
              var month=(newDate.getMonth())+1;
              var monthTxt=month.toString();
              if(month<10){monthTxt='0'+monthTxt};
              var day=newDate.getDate();
              var dayTxt=day.toString();
              if(day<10){dayTxt='0'+dayTxt};
              var hour=newDate.getHours();
              var hourTxt=hour.toString();
              if(hour<10){hourTxt='0'+hourTxt};
              var minute=newDate.getMinutes();
              var minuteTxt=minute.toString();
              if(minute<10){minuteTxt='0'+minuteTxt};
              var second=newDate.getSeconds();
              var secondTxt=second.toString();
              if(second<10){secondTxt='0'+secondTxt};
              var fullDateTxt=''+dayTxt+'-'+monthTxt+'-'+year+' '+hourTxt+':'+minuteTxt+':'+secondTxt+'';
              for(var i=0;i<tabselp.length;i++){
                var tdx=tabselp[i].index;
                var ttotscan=glist[tdx].totScan;
                var tmaxscan=glist[tdx].maxscan;
                if(ttotscan<tmaxscan){
                  if(glist[tdx].scans[idlist]){
                    if(glist[tdx].scans[idlist].nbrScan){
                      glist[tdx].scans[idlist].nbrScan++;
                      glist[tdx].scans[idlist].dateScan.push({'date':fullDateTxt,'idStaff':'fromBo'});
                    }else{
                      glist[tdx].scans[idlist]['nbrScan']=1;
                      glist[tdx].scans[idlist]['dateScan']=[{'date':fullDateTxt,'idStaff':'fromBo'}];
                    };
                  }else{
                    glist[tdx].scans[idlist]={};
                    glist[tdx].scans[idlist]['nbrScan']=1;
                    glist[tdx].scans[idlist]['dateScan']=[{'date':fullDateTxt,'idStaff':'fromBo'}];
                  };
                  glist[tdx].totScan++;
                  glist[tdx].totScanIn++;
                  glist[tdx].tabScanIn.push({'date':fullDateTxt,'idStaff':'fromBo'});
                  lidm.push({'idParticipant':tabselp[i].id, 'data':[{'scans':glist[tdx].scans}]});
                };
              };
              if(lidm.length>0){
                fetchParticipants();
                dataEditAll=lidm;
                t.boService.editAllParticipants(dataEditAll).then(data => {
                  $('.savestick').css({'right':'-260px'});
                  checlic=0;
                })
                .catch(error => {
                  // faire cas erreur
                });
              }else{
                $('.savestick').css({'right':'-260px'});
                checlic=0;
              };
            }else if(vsa=='decompost'){
              var newDate=new Date();
              var year=newDate.getFullYear();
              var month=(newDate.getMonth())+1;
              var monthTxt=month.toString();
              if(month<10){monthTxt='0'+monthTxt};
              var day=newDate.getDate();
              var dayTxt=day.toString();
              if(day<10){dayTxt='0'+dayTxt};
              var hour=newDate.getHours();
              var hourTxt=hour.toString();
              if(hour<10){hourTxt='0'+hourTxt};
              var minute=newDate.getMinutes();
              var minuteTxt=minute.toString();
              if(minute<10){minuteTxt='0'+minuteTxt};
              var second=newDate.getSeconds();
              var secondTxt=second.toString();
              if(second<10){secondTxt='0'+secondTxt};
              var fullDateTxt=''+dayTxt+'-'+monthTxt+'-'+year+' '+hourTxt+':'+minuteTxt+':'+secondTxt+'';
              for(var i=0;i<tabselp.length;i++){
                var tdx=tabselp[i].index;
                var ttotscan=glist[tdx].totScan;
                if(ttotscan>0){
                  if(glist[tdx].scans[idlist]){
                    if(glist[tdx].scans[idlist].nbrDeScan){
                      glist[tdx].scans[idlist].nbrDeScan++;
                      glist[tdx].scans[idlist].dateDescan.push({'date':fullDateTxt,'idStaff':'fromBo'});
                    }else{
                      glist[tdx].scans[idlist]['nbrDeScan']=1;
                      glist[tdx].scans[idlist]['dateDescan']=[{'date':fullDateTxt,'idStaff':'fromBo'}];
                    };
                  }else{
                    glist[tdx].scans[idlist]={};
                    glist[tdx].scans[idlist]['nbrDeScan']=1;
                    glist[tdx].scans[idlist]['dateDescan']=[{'date':fullDateTxt,'idStaff':'fromBo'}];
                  };
                  glist[tdx].totScan--;
                  glist[tdx].totScanOut++;
                  glist[tdx].tabScanOut.push({'date':fullDateTxt,'idStaff':'fromBo'});
                  lidm.push({'idParticipant':tabselp[i].id, 'data':[{'scans':glist[tdx].scans}]});
                };
              };
              if(lidm.length>0){
                fetchParticipants();
                dataEditAll=lidm;
                t.boService.editAllParticipants(dataEditAll).then(data => {
                  $('.savestick').css({'right':'-260px'});
                  checlic=0;
                })
                .catch(error => {
                  // faire cas erreur
                });
              }else{
                $('.savestick').css({'right':'-260px'});
                checlic=0;
              };
            }else if(vsa=='ban'){
              for(var i=0;i<tabselp.length;i++){
                if(glist[tabselp[i].index].ban=='true'){
                  glist[tabselp[i].index].ban='false';
                   lidm.push({'idParticipant':tabselp[i].id, 'data':[{'ban':'false'}]});
                }else{
                  glist[tabselp[i].index].ban='true';
                   lidm.push({'idParticipant':tabselp[i].id, 'data':[{'ban':'true'}]});
                };
              };
              if(lidm.length>0){
                fetchParticipants();
                dataEditAll=lidm;
                t.boService.editAllParticipants(dataEditAll).then(data => {
                  $('.savestick').css({'right':'-260px'});
                  checlic=0;
                })
                .catch(error => {
                  // faire cas erreur
                });
              }else{
                $('.savestick').css({'right':'-260px'});
                checlic=0;
              };
            }else if(vsa=='changetpl'){
              $('.savestick').css({'right':'-260px'});
              var bulkIdEvent = tabselp.length > 0 ? tabselp[0].idEvent : null;
              if(!bulkIdEvent){ checlic=0; return; };
              (window as any).__wiloutBulkTpl = function(newIdTpl: string){
                delete (window as any).__wiloutBulkTpl;
                var participantIds = tabselp.map(function(p: any){ return p.id; });
                $('.bulk-tpl-search-list').css({'pointer-events':'none','opacity':'0.5'});
                t.boService.bulkChangeTpl(idlist, participantIds, newIdTpl).then(function(data: any){
                  $('.bulk-tpl-panel').hide();
                  $('.savestick').css({'right':'0px'});
                  setTimeout(function(){ $('.savestick').css({'right':'-260px'}); }, 2500);
                  fetchParticipants();
                  checlic=0;
                  var skipped = (data && typeof data.skipped === 'number') ? data.skipped : 0;
                  t.showTplNotice(t.bulkTplSummary(data).join('<br>'), skipped > 0 ? 'error' : 'info');
                }).catch(function(err: any){
                  console.error('Bulk tpl error', err);
                  $('.bulk-tpl-panel').hide();
                  checlic=0;
                  t.showTplNotice('Erreur lors du changement de tarif en masse', 'error');
                });
              };
              var renderBulkList2 = function(list: any[]){
                var html = list.map(function(tpl: any){
                  return '<div onclick="window.__wiloutBulkTpl(\''+tpl._id+'\')" style="padding:8px 12px;cursor:pointer;border-bottom:1px solid #eee;">'+tpl.name+'</div>';
                }).join('');
                $('.bulk-tpl-search-list').css({'pointer-events':'auto','opacity':'1'}).html(html);
              };
              t.boService.getFreeTplByEvent(bulkIdEvent).then(function(data: any){
                var allBulkTpl: any[] = (data?.message || []);
                if(allBulkTpl.length === 0){ checlic=0; return; };
                renderBulkList2(allBulkTpl);
                $('.bulk-tpl-panel').css('display','flex');
                setTimeout(function(){ $('.input-bulk-tpl-search').val('').focus(); }, 50);
                $('.bulk-tpl-close').off('click').on('click', function(){
                  delete (window as any).__wiloutBulkTpl;
                  $('.bulk-tpl-panel').hide();
                  checlic=0;
                });
                $('.input-bulk-tpl-search').off('input').on('input', function(){
                  var t2 = $(this).val().toString().toLowerCase();
                  renderBulkList2(allBulkTpl.filter(function(tpl: any){ return tpl.name.toLowerCase().indexOf(t2) !== -1; }));
                });
                checlic=0;
              }).catch(function(err: any){
                console.error('Erreur tpl gratuits', err);
                checlic=0;
              });
            };
          }else{
            $('.txtnoX').remove();
            $('.searchPar').val('');
            term='';
            searchLP();
            // ajout participant;
          };
        };
      });
      var term='';
      var checksearch=0;
      // searchLP is already defined as a wrapper above — no second definition needed
      $('.cleanSearch').click(function(){
        $('.searchPar').val('');
        term='';
        p=1;
        fetchParticipants();
      });
      function refreshIndex(){
        for(var i=0;i<glist.length;i++){glist[i].index=i;};
      };
      $('.searchPar').keyup(function(){
        term=$(this).val().toString()||'';
        // Debounce server-side search to avoid flooding API
        if(searchTimer){clearTimeout(searchTimer)};
        searchTimer=setTimeout(function(){
          p=1;
          fetchParticipants();
        }, 300);
      });
      // Sort field map from column tag to server field name
      var sortFieldMapFront={'1':'date','2':'name','3':'email','4':'tpl','5':'keyRandom','6':'date'};
      $('.htc').click(function(){
        var th=$(this);
        var gt=th.attr('tag');
        if(!gt) return;
        var newSortField=sortFieldMapFront[gt]||'date';
        if(th.attr('id')=='triondown'){
          th.attr('id','trionup');
          sortField=newSortField;
          sortOrder='asc';
        }else if(th.attr('id')=='trionup'){
          th.attr('id','triondown');
          sortField=newSortField;
          sortOrder='desc';
        }else{
          $('.htc').attr('id','');
          th.attr('id','triondown');
          sortField=newSortField;
          sortOrder='desc';
        };
        p=1;
        fetchParticipants();
      });
      var dataCustomer={};
      var checlms=0;
      $('.contPar').on('click','.lscan,.mscan',function(){
        if(checlms==0){
          checlms=1;
          var th=$(this);
          var tclass=th.attr('class');
          var tpar=th.parent().parent().parent();
          var tid=tpar.attr('id');
          var tindex=tpar.attr('index');
          var ttotscan=glist[tindex].totScan;
          var tmaxscan=glist[tindex].maxscan;
          var tcs=tpar.find('.circlescan');
          var tns=tpar.find('.nbscan');
          var cs='cs1';
          var newDate=new Date();
          var year=newDate.getFullYear();
          var month=(newDate.getMonth())+1;
          var monthTxt=month.toString();
          if(month<10){monthTxt='0'+monthTxt};
          var day=newDate.getDate();
          var dayTxt=day.toString();
          if(day<10){dayTxt='0'+dayTxt};
          var hour=newDate.getHours();
          var hourTxt=hour.toString();
          if(hour<10){hourTxt='0'+hourTxt};
          var minute=newDate.getMinutes();
          var minuteTxt=minute.toString();
          if(minute<10){minuteTxt='0'+minuteTxt};
          var second=newDate.getSeconds();
          var secondTxt=second.toString();
          if(second<10){secondTxt='0'+secondTxt};
          var fullDateTxt=''+dayTxt+'-'+monthTxt+'-'+year+' '+hourTxt+':'+minuteTxt+':'+secondTxt+'';
          if((tclass=='lscan'&&ttotscan>0)||(tclass=='mscan'&&ttotscan<tmaxscan)){
            setTimeout(function(){
              if(checlms==1){
                th.html('<i class="fa-solid fa-spinner-third fa-spin"></i>');
              };
            },500);
            dataCustomer['scans']=Object.assign({},glist[tindex].scans);
            if(tclass=='lscan'){
              if(glist[tindex].scans[idlist]){
                if(dataCustomer['scans'][idlist].nbrDeScan){
                  dataCustomer['scans'][idlist].nbrDeScan++;
                  dataCustomer['scans'][idlist].dateDescan.push({'date':fullDateTxt,'idStaff':'fromBo'});
                }else{
                  dataCustomer['scans'][idlist]['nbrDeScan']=1;
                  dataCustomer['scans'][idlist]['dateDescan']=[{'date':fullDateTxt,'idStaff':'fromBo'}];
                };
              }else{
                dataCustomer['scans'][idlist]={};
                dataCustomer['scans'][idlist]['nbrDeScan']=1;
                dataCustomer['scans'][idlist]['dateDescan']=[{'date':fullDateTxt,'idStaff':'fromBo'}];
              };
            }else if(tclass=='mscan'){
              if(glist[tindex].scans[idlist]){
                if(dataCustomer['scans'][idlist].nbrScan){
                  dataCustomer['scans'][idlist].nbrScan++;
                  dataCustomer['scans'][idlist].dateScan.push({'date':fullDateTxt,'idStaff':'fromBo'});
                }else{
                  dataCustomer['scans'][idlist]['nbrScan']=1;
                  dataCustomer['scans'][idlist]['dateScan']=[{'date':fullDateTxt,'idStaff':'fromBo'}];
                };
              }else{
                dataCustomer['scans'][idlist]={};
                dataCustomer['scans'][idlist]['nbrScan']=1;
                dataCustomer['scans'][idlist]['dateScan']=[{'date':fullDateTxt,'idStaff':'fromBo'}];
              };
            };
            tns.css({'transform':'scale(1.5) translateX(-3px)'});
            t.boService.editParticipant(tid,dataCustomer).then(data => {
              glist[tindex].scans=dataCustomer['scans'];
              if(tclass=='lscan'){
                glist[tindex].totScan--;
                glist[tindex].totScanOut++;
                glist[tindex].tabScanOut.push({'date':fullDateTxt,'idStaff':'fromBo'});
                if(glist[tindex].totScan<=0){cs='cs0';};
                th.html('<i class="fa-solid fa-minus"></i>');
              }else if(tclass=='mscan'){
                glist[tindex].totScan++;
                glist[tindex].totScanIn++;
                glist[tindex].tabScanIn.push({'date':fullDateTxt,'idStaff':'fromBo'});
                if(glist[tindex].totScan>=glist[tindex].maxscan){cs='cs2';};
                th.html('<i class="fa-solid fa-plus"></i>');
              };
              tcs.attr('class','circlescan '+cs+'');
              tns.html(glist[tindex].totScan);
              setTimeout(function(){tns.css({'transform':'scale(1.0) translateX(0px)'});},200);
              dataCustomer={};
              checlms=0;
            }).catch(error => {
              dataCustomer={};
            });
          }else{
            tcs.css({'transform':'rotate(25deg)'});
            setTimeout(function(){tcs.css({'transform':'rotate(-15deg)'});},150);
            setTimeout(function(){tcs.css({'transform':'rotate(10deg)'});},300);
            setTimeout(function(){tcs.css({'transform':'rotate(-5deg)'});},450);
            setTimeout(function(){tcs.css({'transform':'rotate(0deg)'});},600);
            checlms=0;
          };
        };
      });
      function detpar(){
        $(".ltcdate,.ltcname,.ltcmail,.ltctpl,.ltckey,.ltcnfc,.ltcnfclong").click(function(){
          var idxStr = $(this).parent().attr("index");
          t.initDrawerRefs(glist, idlist, dataKeys, function(){ fetchParticipants(); });
          t.openDrawerForIndex(parseInt(idxStr, 10));
        });
      };
      // ---------------------------------------------------------
    });
  };

}
